import type { Capability } from '@rtkwlf/capability';
import type { Organization } from '@rtkwlf/eloc';
import type {
  Capabilities as LegacyCapabilities,
  OrganizationFlags,
  Organization as RendallOrganization,
  SubscriptionDetail,
  User,
} from '@rtkwlf/rendall';
import type { TFunction } from 'i18next';

import { hasRequiredCapability } from '@apis/capability/has-required-capability/has-required-capability';
import type { MenuItem } from '@packages/components/navigation/navigation.constants';
import type {
  FeatureFlagKey,
  FeatureFlagsRecord,
} from '@packages/platform/feature-flags/feature-flags';
import { hasAnyRequiredProperty } from '@packages/utils/has-any-required-property';
import {
  ACCOUNT_CONFIG_FEDERATED_AUTHENTICATION,
  ACCOUNT_CONFIG_RULES_ROUTE,
  ACCOUNT_CONTACTS_ROUTE,
  ACCESS_CONTROL_ROUTE,
  ACCOUNT_ESCALATIONS_ROUTE,
  ACCOUNT_INTEGRATIONS_ROUTE,
  ACCOUNT_ROUTE,
  ACCOUNT_SITES_ROUTE,
  ADS_ROUTE_ASSETS,
  ALLOW_LIST_REQUIREMENTS_ROUTE,
  CONTACT_CONCIERGE_ROUTE,
  CYBER_RESILIENCE_ASSESSMENT_ROUTE,
  DATA_EXPLORER_ROUTE,
  DATA_FLOW_ROUTE,
  DETECTION_CATALOG_ROUTE,
  RISK_MANAGEMENT_RISK_EXPOSURE_OVERVIEW_ROUTE,
  RISK_MANAGEMENT_RISKS_ROUTE,
  INSIGHTS_ROUTE,
  LOG_SEARCH_HISTORY_ROUTE,
  LOG_SEARCH_ROUTE,
  LOG_SEARCH_SOURCES_ROUTE,
  REPORTS_ROUTE,
  RESOURCE_CENTER_DOWNLOADS_ROUTE,
  RESOURCE_CENTER_ROUTE,
  SCHEDULED_REPORTS_ROUTE,
  SECURITY_FOCUSES_ROUTE,
  SECURITY_JOURNEY_ROUTE,
  SPIDR_LIBRARY_ROUTE,
  TELEMETRY_AGENTS_ROUTE,
  TELEMETRY_CLOUD_SENSORS_ROUTE,
  TELEMETRY_LOG_SOURCES_ROUTE,
  TELEMETRY_OVERVIEW_ROUTE,
  TELEMETRY_SCAN_SCHEDULES_ROUTE,
  TELEMETRY_SCANNERS_ROUTE,
  TELEMETRY_SENSORS_ROUTE,
  THREAT_INTEL_SKU_BULLETINS_PATH,
  TICKETS_ROUTE,
  UNIFIED_CONTENT_LIBRARY_CATALOG_SEARCH_ROUTE,
  ACCOUNT_ONBOARDING_ROUTE,
  INCIDENT_RESPONSE_ROUTE,
  CRA_FILE_REPOSITORY_ROUTE,
  RISK_MANAGEMENT_OVERVIEW_ROUTE,
  CYBER_RESILIENCE_ASSESSMENT_COLLABORATORS_ROUTE,
} from 'ae-constants/routes.constants';

/** subscription & user will be deprecated for capabilities */
const checkLegacyCapabilities = (
  t: TFunction,
  item: MenuItem,
  subscription: SubscriptionDetail | undefined,
  user: User | undefined,
  legacyCapabilities: LegacyCapabilities | undefined,
  featureFlags: Record<string, boolean>,
): boolean => {
  if (item.text === t('navMenu:riskManagement')) {
    return (
      hasAnyRequiredProperty(item.legacyCapabilities, legacyCapabilities) ||
      hasAnyRequiredProperty(item.requires, subscription)
    );
  }

  if (item.text === t('navMenu:dataExploration')) {
    return (
      hasAnyRequiredProperty(item.legacyCapabilities, legacyCapabilities) ||
      hasAnyRequiredProperty(item.requires, subscription) ||
      hasAnyRequiredProperty(item.userPermissions, user, false)
    );
  }

  if (item.text === t('navMenu:insights')) {
    return hasAnyRequiredProperty(
      item.legacyCapabilities,
      legacyCapabilities,
      false,
    );
  }

  return (
    hasAnyRequiredProperty(item.legacyCapabilities, legacyCapabilities) &&
    hasAnyRequiredProperty(item.requires, subscription) &&
    hasAnyRequiredProperty(item.userPermissions, user)
  );
};

const getDefaultMenu = (
  t: TFunction,
  legacyCapabilities: LegacyCapabilities | undefined,
  featureFlags: FeatureFlagsRecord,
  dataExplorerMenuItem: MenuItem,
  rendallFeatureFlags: OrganizationFlags,
): MenuItem[] => {
  return [
    {
      text: t('navMenu:dashboard'),
      to: '/',
    },
    {
      legacyCapabilities: {
        canViewAgent: true,
        canViewAllTelemetry: true,
        canViewCloudSensor: true,
        canViewScanSchedules: true,
        canViewScanner: true,
        canViewSensor: true,
        canViewVirtualAppliances: true,
      },
      submenu: [
        {
          legacyCapabilities: {
            canViewAllTelemetry: true,
          },
          end: true,
          text: t('navMenu:healthOverview'),
          to: TELEMETRY_OVERVIEW_ROUTE,
        },
        {
          legacyCapabilities: {
            canViewSensor: true,
          },
          text: t('navMenu:sensors'),
          to: TELEMETRY_SENSORS_ROUTE,
        },
        {
          legacyCapabilities: {
            canViewScanner: true,
          },
          text: t('navMenu:scanners'),
          to: TELEMETRY_SCANNERS_ROUTE,
        },
        {
          legacyCapabilities: {
            canViewAgent: true,
          },
          text: t('navMenu:agents'),
          to: TELEMETRY_AGENTS_ROUTE,
        },
        {
          legacyCapabilities: {
            canViewCloudSensor: true,
          },
          text: t('navMenu:connectedAccounts'),
          to: TELEMETRY_CLOUD_SENSORS_ROUTE,
        },
        {
          legacyCapabilities: {
            canViewScanSchedules: true,
          },
          text: t('navMenu:scanSchedules'),
          to: TELEMETRY_SCAN_SCHEDULES_ROUTE,
        },
        {
          legacyCapabilities: {
            canViewVirtualAppliances: true,
          },
          customerParam: 'cid={customerID}',
          target: '_blank',
          text: t('navMenu:virtualAppliances'),
          to: 'https://portal.arcticwolf.com/dashboard/sensors/virtual',
        },
        {
          text: t('navMenu:logSources'),
          to: TELEMETRY_LOG_SOURCES_ROUTE,
        },
      ],
      text: t('navMenu:telemetryManagement'),
      to: TELEMETRY_OVERVIEW_ROUTE,
    },
    {
      legacyCapabilities: featureFlags['ADS_GA_RELEASE']
        ? {
            canAccessManageRisk: true,
          }
        : { assets: true },
      featureFlag: 'ADS_ASSETS',
      text: t('ads:myAssets'),
      to: ADS_ROUTE_ASSETS,
    },
    {
      legacyCapabilities: {
        canAccessAnalyzedData: true,
        canAccessAnalyzedDataLite: true,
        canAccessDataFlow: true,
        canAccessRawData: true,
      },
      requires: { managedDetectionResponse: true },
      submenu: [
        dataExplorerMenuItem,
        {
          legacyCapabilities: { canAccessRawData: true },
          text: t('navMenu:rawLogSearch'),
          to: LOG_SEARCH_ROUTE,
        },
        {
          legacyCapabilities: { canAccessRawData: true },
          text: t('navMenu:rawLogHistory'),
          to: LOG_SEARCH_HISTORY_ROUTE,
        },
        {
          legacyCapabilities: { canAccessRawData: true },
          text: t('navMenu:rawLogSources'),
          to: LOG_SEARCH_SOURCES_ROUTE,
        },
        {
          legacyCapabilities: { canAccessDataFlow: true },
          text: t('navMenu:flowData'),
          to: DATA_FLOW_ROUTE,
        },
      ],
      text: t('navMenu:dataExploration'),
      to: DATA_EXPLORER_ROUTE,
    },
    {
      text: t('navMenu:tickets'),
      to: TICKETS_ROUTE,
    },
    {
      legacyCapabilities: {
        detectionLibrary: true,
      },
      requires: {
        managedRisk: true,
      },
      submenu:
        featureFlags['RISKS_IN_UP_M1'] && rendallFeatureFlags['risksInUpM1']
          ? [
              {
                text: t('navMenu:riskExposureOverview'),
                to: RISK_MANAGEMENT_RISK_EXPOSURE_OVERVIEW_ROUTE,
              },
              {
                text: t('navMenu:risks'),
                to: RISK_MANAGEMENT_RISKS_ROUTE,
              },
              {
                legacyCapabilities: {
                  detectionLibrary: true,
                },
                text: t('navMenu:detectionLibrary'),
                to: UNIFIED_CONTENT_LIBRARY_CATALOG_SEARCH_ROUTE,
              },
              {
                customerParam: 'id={orgID}',
                requires: {
                  managedRisk: true,
                },
                target: '_blank',
                text: t('navMenu:legacyRiskDashboard'),
                to: 'https://risk.arcticwolf.com',
              },
            ]
          : [
              {
                customerParam: 'id={orgID}',
                requires: {
                  managedRisk: true,
                },
                target: '_blank',
                text: t('navMenu:viewAllRisks'),
                to: 'https://risk.arcticwolf.com/risks',
              },
              {
                customerParam: 'id={orgID}',
                requires: {
                  managedRisk: true,
                },
                target: '_blank',
                text: t('navMenu:viewMyAssets'),
                to: 'https://risk.arcticwolf.com/assets',
              },
              {
                customerParam: 'id={orgID}',
                requires: {
                  managedRisk: true,
                },
                target: '_blank',
                text: t('navMenu:riskScoreOverview'),
                to: 'https://risk.arcticwolf.com',
              },
              {
                customerParam: 'id={orgID}',
                requires: {
                  managedRisk: true,
                },
                target: '_blank',
                text: t('navMenu:externalVulnerabilityAssessment'),
                to: 'https://risk.arcticwolf.com/reach.html',
              },
              {
                legacyCapabilities: {
                  detectionLibrary: true,
                },
                text: t('navMenu:detectionLibrary'),
                to: UNIFIED_CONTENT_LIBRARY_CATALOG_SEARCH_ROUTE,
              },
            ],
      text: t('navMenu:riskManagement'),
      to: RISK_MANAGEMENT_OVERVIEW_ROUTE,
    },
    {
      legacyCapabilities: {
        canViewAllTickets: true,
      },
      submenu: [
        {
          end: true,
          text: t('navMenu:pastReports'),
          to: REPORTS_ROUTE,
        },
        {
          text: t('navMenu:scheduledReports'),
          to: SCHEDULED_REPORTS_ROUTE,
        },
        {
          legacyCapabilities: { canAccessSelfServiceReporting: true },
          text: t('navMenu:dashboards'),
          to: INSIGHTS_ROUTE,
        },
        {
          text: t('navMenu:threatIntelligence'),
          to: THREAT_INTEL_SKU_BULLETINS_PATH,
          featureFlag: 'THREAT_INTEL_SKU',
        },
      ],
      text: legacyCapabilities?.canAccessSelfServiceReporting
        ? t('navMenu:dashboardAndReports')
        : t('navMenu:reports'),
      to: REPORTS_ROUTE,
    },
    {
      submenu: [
        {
          text: t('navMenu:downloads'),
          to: RESOURCE_CENTER_DOWNLOADS_ROUTE,
        },
        {
          legacyCapabilities: { canAccessCustomerCommunity: true },
          target: '_blank',
          text: t('navMenu:arcticCircleCustomerCommunity'),
          to: 'https://community.arcticwolf.com/private/login',
        },
        {
          text: t('navMenu:allowlistRequirements'),
          to: ALLOW_LIST_REQUIREMENTS_ROUTE,
        },
        {
          text: t('navMenu:contactYourConciergeTeam'),
          to: CONTACT_CONCIERGE_ROUTE,
        },
      ],
      text: '',
      to: RESOURCE_CENTER_ROUTE,
    },
    {
      submenu: [
        featureFlags.NEW_ONBOARDING_EXPERIENCE
          ? {
              text: t('navMenu:onboarding'),
              to: ACCOUNT_ONBOARDING_ROUTE,
              featureFlag: 'NEW_ONBOARDING_EXPERIENCE',
            }
          : {
              customerParam: 'cid={customerID}',
              target: '_blank',
              text: t('navMenu:onboarding'),
              to: 'https://portal.arcticwolf.com/onboarding',
            },
        {
          text: t('navMenu:contacts'),
          to: ACCOUNT_CONTACTS_ROUTE,
        },
        {
          text: t('navMenu:sites'),
          to: ACCOUNT_SITES_ROUTE,
        },
        {
          requires: { managedRisk: true, managedDetectionResponse: true },
          text: t('navMenu:escalations'),
          to: ACCOUNT_ESCALATIONS_ROUTE,
        },
        {
          text: t('navMenu:configurationRules'),
          to: ACCOUNT_CONFIG_RULES_ROUTE,
        },
        {
          text: t('navMenu:federatedAuthentication'),
          to: ACCOUNT_CONFIG_FEDERATED_AUTHENTICATION,
        },
        {
          text: t('navMenu:integrations'),
          to: ACCOUNT_INTEGRATIONS_ROUTE,
        },
        {
          text: t('navMenu:accessControl'),
          to: ACCESS_CONTROL_ROUTE,
          featureFlag: 'ACCESS_CONTROL',
        },
      ],
      text: '',
      to: ACCOUNT_ROUTE,
    },
    {
      text: t('navMenu:securityJourney'),
      to: SECURITY_JOURNEY_ROUTE,
      submenu: [
        {
          text: t('navMenu:securityFocuses'),
          to: SECURITY_FOCUSES_ROUTE,
        },
        {
          text: t('navMenu:spidrLibrary'),
          to: SPIDR_LIBRARY_ROUTE,
        },
        {
          text: t('navMenu:cyberResilienceAssessment'),
          to: CYBER_RESILIENCE_ASSESSMENT_ROUTE,
          featureFlag: 'CYBER_RESILIENCE_ASSESSMENT',
        },
        {
          text: t('navMenu:collaborators'),
          to: CYBER_RESILIENCE_ASSESSMENT_COLLABORATORS_ROUTE,
          featureFlag: 'CYBER_RESILIENCE_ASSESSMENT',
        },
        {
          text: t('navMenu:fileRepository'),
          to: CRA_FILE_REPOSITORY_ROUTE,
          featureFlag: 'CYBER_RESILIENCE_ASSESSMENT',
        },
      ],
    },
    {
      text: t('navMenu:incidentResponse'),
      to: INCIDENT_RESPONSE_ROUTE,
      featureFlag: 'INCIDENT_RESPONSE',
      legacyCapabilities: {
        canAccessIncidentResponse: true,
      },
    },
    {
      text: t('navMenu:detectionCatalog'),
      to: DETECTION_CATALOG_ROUTE,
      systemCapability: 'detection_catalog',
      featureFlag: 'DETECTION_CATALOG_V2',
    },
  ];
};

export type NavDataProps = {
  t: TFunction;
  organizations: Organization[] | undefined;
  organization?: RendallOrganization;
  orgId: string | undefined;
  subscriptions: SubscriptionDetail | undefined;
  capabilities: Capability[] | undefined;
  systemCapabilities: Capability[] | undefined;
  legacyCapabilities: LegacyCapabilities | undefined;
  user: User | undefined;
  menu?: MenuItem[];
  featureFlags: FeatureFlagsRecord;
  condition?: boolean;
  dataExplorerMenuItem: MenuItem;
};

function generateQueryParam(
  organizations: Organization[],
  customerParam: string | undefined,
  customerId: string,
  orgId: string,
) {
  if (organizations.length === 1) {
    return '';
  }

  if (!customerParam) {
    return '';
  }

  if (!customerId) {
    return '';
  }

  if (!orgId) {
    return '';
  }

  return `?${customerParam}`
    .replace('{customerID}', customerId)
    .replace('{orgID}', orgId);
}

export const getNavData = ({
  t,
  organizations = [],
  organization,
  orgId = '',
  subscriptions,
  user,
  menu: propMenu,
  featureFlags,
  capabilities,
  systemCapabilities,
  legacyCapabilities,
  dataExplorerMenuItem,
}: NavDataProps): MenuItem[] => {
  const menu =
    propMenu ||
    getDefaultMenu(
      t,
      legacyCapabilities,
      featureFlags,
      dataExplorerMenuItem,
      organization?.flags || {},
    );

  const hasFeatureFlag = (featureFlag: FeatureFlagKey) =>
    featureFlags[featureFlag];

  const hasRendallFeatureFlags = (rendallFeatureFlag: string) =>
    !!organization?.flags[rendallFeatureFlag as keyof OrganizationFlags];

  return menu
    .filter((item) =>
      checkLegacyCapabilities(
        t,
        item,
        subscriptions,
        user,
        legacyCapabilities,
        featureFlags,
      ),
    )
    .filter(
      (item) =>
        (!item.capability ||
          hasRequiredCapability(item.capability, capabilities)) &&
        (!item.systemCapability ||
          hasRequiredCapability(item.systemCapability, systemCapabilities)),
    )
    .filter(({ featureFlag }) => !featureFlag || hasFeatureFlag(featureFlag))
    .filter(
      ({ hideFeatureFlag }) =>
        !hideFeatureFlag || !hasFeatureFlag(hideFeatureFlag),
    )
    .filter(
      ({ rendallFeatureFlag }) =>
        !rendallFeatureFlag || hasRendallFeatureFlags(rendallFeatureFlag),
    )
    .map((item) => {
      const queryParam = generateQueryParam(
        organizations,
        item.customerParam,
        organization?.customerID || '', // TODO S2CX-1949 Bad, code does not reflect the actual lifecycle of the application
        orgId,
      );

      return {
        ...item,
        submenu: item.submenu
          ? getNavData({
              legacyCapabilities,
              capabilities,
              systemCapabilities,
              organization,
              featureFlags,
              menu: item.submenu,
              orgId,
              organizations,
              subscriptions,
              t,
              user,
              dataExplorerMenuItem,
            })
          : undefined,
        to: `${item.to}${queryParam}`,
      };
    });
};
